/*
Origin Core 1.0

called from /snippets/header-icons.liquid

DEVELOPER NOTE
--------------------
any changes to this file should be noted here, and also noted in the following wiki page:
https://unifiedco.atlassian.net/wiki/spaces/ORIGIN/pages/1413414932/Core+File+Changes+1.0

SUMMARY
--------------------
This script defines a custom web component <cart-icon> that displays and updates the cart icon and bubble in the Shopify header. 
It listens for cart update events, updates the cart count, manages animation for the cart bubble, and stores the cart count in session storage for consistency. 
The component ensures the cart bubble reflects the correct item count and handles UI changes when items are added or removed. 
It is registered as a custom element if not already defined.

*/

import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText'];

  /** @type {number} */
  get currentCartCount() {
    return Number(this.dataset.count) || 0;
  }

  set currentCartCount(value) {
    this.dataset.count = String(value);
    this.refs.cartBubbleText.textContent = value < 100 ? String(value) : '99+';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener(ThemeEvents.cartAdd, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener(ThemeEvents.cartAdd, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const data = event.detail?.data || {};
    // Section HTML carries the same visible total used by the drawer, excluding
    // gift wrap and personalisation children. An add event's itemCount is a delta.
    for (const section of Object.values(data.sections || {})) {
      if (typeof section !== 'string') continue;
      const html = new DOMParser().parseFromString(section, 'text/html');
      const text = html.querySelector('[ref="cartItemCount"]')?.textContent?.trim();
      if (text && /^\d+$/.test(text)) {
        this.renderCartBubble(Number(text), false);
        return;
      }
    }
    // Failed adds can partially succeed; use section totals above when supplied,
    // but never blindly add the requested quantity after an error.
    if (data.didError || !Number.isInteger(data.itemCount) || data.itemCount < 0) return;
    this.renderCartBubble(data.itemCount, event.type === ThemeEvents.cartAdd);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} isDelta - Whether itemCount is an added quantity rather than the cart total.
   */
  renderCartBubble = async (itemCount, isDelta, animate = true) => {
    // Add only when no authoritative total was supplied for an add event.
    const total = isDelta ? this.currentCartCount + itemCount : itemCount;
    this.refs.cartBubble.classList.toggle('sr-only', total === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', total > 0 && animate);
    this.currentCartCount = total;

    this.classList.toggle('header-actions__cart-icon--has-cart', total > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleText.textContent;
    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
